/**
 * Servicio de Cheques
 * Maneja todas las operaciones CRUD y consultas de cheques
 */
import api from './api';

/**
 * Obtener todos los cheques con filtros opcionales
 * @param {Object} filters - Filtros de búsqueda (clienteId, bancoId, estado, fechaDesde, fechaHasta, etc.)
 * @returns {Promise<Array>} Lista de cheques
 */
export const getAllCheques = async (filters = {}) => {
  try {
    const response = await api.get('/cheques', {
      params: filters,
    });
    // El backend retorna { success: true, data: { cheques: [...], pagination: {...} } }
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheques:', error);
    throw error;
  }
};

/**
 * Obtener un cheque por ID
 * @param {string} id - ID del cheque
 * @returns {Promise<Object>} Datos del cheque
 */
export const getChequeById = async (id) => {
  try {
    const response = await api.get(`/cheques/${id}`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheque:', error);
    throw error;
  }
};

/**
 * Crear un nuevo cheque
 * @param {Object} data - Datos del cheque
 * @returns {Promise<Object>} Cheque creado
 */
export const createCheque = async (data) => {
  try {
    const response = await api.post('/cheques', data);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al crear cheque:', error);
    throw error;
  }
};

/**
 * Actualizar un cheque existente
 * @param {string} id - ID del cheque
 * @param {Object} data - Datos actualizados del cheque
 * @returns {Promise<Object>} Cheque actualizado
 */
export const updateCheque = async (id, data) => {
  try {
    const response = await api.put(`/cheques/${id}`, data);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al actualizar cheque:', error);
    throw error;
  }
};

/**
 * Eliminar un cheque
 * @param {string} id - ID del cheque
 * @returns {Promise<void>}
 */
export const deleteCheque = async (id) => {
  try {
    const response = await api.delete(`/cheques/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar cheque:', error);
    throw error;
  }
};

/**
 * Obtener cheques pendientes con filtros
 * @param {Object} filters - Filtros (clienteId, bancoId, fechaDesde, fechaHasta)
 * @returns {Promise<Array>} Lista de cheques pendientes
 */
export const getPendingChecks = async (filters = {}) => {
  try {
    const response = await api.get('/cheques/pendientes', {
      params: filters,
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheques pendientes:', error);
    throw error;
  }
};

/**
 * Obtener cheques posfechados
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Array>} Lista de cheques posfechados
 */
export const getPostDatedChecks = async (filters = {}) => {
  try {
    const response = await api.get('/cheques/posfechados', {
      params: filters,
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheques posfechados:', error);
    throw error;
  }
};

/**
 * Obtener cheques por rango de fechas
 * @param {string} fechaDesde - Fecha inicio (ISO)
 * @param {string} fechaHasta - Fecha fin (ISO)
 * @returns {Promise<Array>} Lista de cheques
 */
export const getChequesByDateRange = async (fechaDesde, fechaHasta) => {
  try {
    const response = await api.get('/cheques', {
      params: { fechaDesde, fechaHasta },
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheques por rango de fechas:', error);
    throw error;
  }
};

/**
 * Obtener cheques por cliente
 * @param {string} clienteId - ID del cliente
 * @returns {Promise<Array>} Lista de cheques del cliente
 */
export const getChequesByCliente = async (clienteId) => {
  try {
    const response = await api.get('/cheques', {
      params: { clienteId },
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheques por cliente:', error);
    throw error;
  }
};

/**
 * Obtener cheques por banco
 * @param {string} bancoId - ID del banco
 * @returns {Promise<Array>} Lista de cheques del banco
 */
export const getChequesByBanco = async (bancoId) => {
  try {
    const response = await api.get('/cheques', {
      params: { bancoId },
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheques por banco:', error);
    throw error;
  }
};

/**
 * Obtener cheques por estado
 * @param {string} estado - Estado del cheque (PENDIENTE, DEPOSITADO, RECHAZADO, etc.)
 * @returns {Promise<Array>} Lista de cheques con ese estado
 */
export const getChequesByEstado = async (estado) => {
  try {
    const response = await api.get('/cheques', {
      params: { estado },
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cheques por estado:', error);
    throw error;
  }
};

/**
 * Cambiar estado de un cheque
 * @param {string} id - ID del cheque
 * @param {string} estado - Nuevo estado
 * @returns {Promise<Object>} Cheque actualizado
 */
export const cambiarEstadoCheque = async (id, estado) => {
  try {
    const response = await api.patch(`/cheques/${id}/estado`, { estado });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al cambiar estado del cheque:', error);
    throw error;
  }
};

export default {
  getAllCheques,
  getChequeById,
  createCheque,
  updateCheque,
  deleteCheque,
  getPendingChecks,
  getPostDatedChecks,
  getChequesByDateRange,
  getChequesByCliente,
  getChequesByBanco,
  getChequesByEstado,
  cambiarEstadoCheque,
};
